import React from "react";
import { Box, Button, FormControl, MenuItem, Select, Typography } from "@mui/material";
import axios from "axios";
import { baseUrl } from "@/consts";
import type { OrderWithOrderBox, OrderTrackApiResponse, TrackingRecord } from "@/components/pages/ShippingPortal/interface";

const REPORTS = [
  "Orders",
  "Offsite Orders",
  // "Product Detail",
  "Inventory Counts",
  "Product Inventory",
  "Financial Reports",
  "Shipping Reports"
];

const placeholderColor = '#6b7280';
const valueColor = '#2d3748';

/** List item from GET InventoryCounts */
export interface InventoryCountListItem {
  inventoryCountId: number;
  name: string;
  dateStarted: string | null;
  dateClosed: string | null;
  createdBy: string;
  createdOn: string;
}

export interface InventoryCountListResponse {
  data: InventoryCountListItem[];
  page: number | null;
  pageSize: number | null;
  totalCount: number | null;
  totalPages: number | null;
}

/** Row from getproducts for one inventory count */
export interface InventoryCountProductRow {
  productId: number;
  productDescription?: string;
  sku?: string;
  inventoryCount?: number;
  inventoryCountWithInventoryCountProducts?: Array<{
    inventoryCountProductId?: number;
    counted?: number;
    inventoryCountProductCreatedBy?: string;
  }>;
}

export interface OffsiteOrderRow {
  orderId: number;
  orderIdentifier: string;
  dealerId: number;
  masterDealerId: string;
  total: number;
  coopUsed: number;
  creditCardUsed: number;
  ship: boolean;
  shippingAddress: string;
  shippingCity: string;
  shippingState: string;
  shippingZip: string;
  dateTimeCreated: string;
  trackingNumber: string;
  couponUsed: number;
  claimFee: number;
  shippingFee: number;
  discount: number;
  discountComment: string | null;
  estimatedDeliveryDate: string;
  numberOfBoxes: number;
  status: number;
  offsite: boolean;
}

const REPORTS_ORDERS = "Orders";
const REPORTS_OFFSITE_ORDERS = "Offsite Orders";
const REPORTS_INVENTORY_COUNTS = "Inventory Counts";
const REPORTS_PRODUCT_INVENTORY = "Product Inventory";
const REPORTS_FINANCIAL_REPORT = "Financial Reports";
const REPORTS_SHIPPING_REPORTS = "Shipping Reports";
const offsiteOrdersBaseUrl = `${process.env.REACT_APP_REST_BASE_URL}/Reports/offsite_orders`;
// Points at the same /products/export endpoint that the Products tab's
// "Export" button hits, so this report and that button return byte-for-byte
// identical CSVs (SKU, Price, ProductDescription, ProductImageUrl,
// QtyReceived, QtySold, QtyInStock, MediaId, Offsite, SupplierName,
// CreatedBy). Avoids maintaining two parallel rollup pipelines.
//
// The old /Reports/product_inventory endpoint + usp_GetProductInventoryReport
// SP returned a simpler 6-column shape and are now unused — safe to delete
// in a follow-up cleanup pass.
const productInventoryBaseUrl = `${process.env.REACT_APP_REST_BASE_URL}/products/export`;
// Reuses the same /Orders/export endpoint that the Orders tab's export button
// hits, so the CSV shape is consistent between the two surfaces. `custom=true`
// returns the "summary" column layout (HeaderCustom in CartDtoMapper.cs) which
// matches what's useful for finance/operations reporting.
const ordersExportBaseUrl = `${process.env.REACT_APP_REST_BASE_URL}/Orders/export`;
// Financial Report — backend endpoint hits usp_GetFinancialReport via
// OrdersService.GetOrdersLineItemReportCsv. One row per OrderProduct line
// joined to dealer/supplier/product type with per-line discount/coop math
// and order-level gift card leftover precomputed by the SP. Defaults to
// current calendar year on the backend if no date range is passed.
const financialReportBaseUrl = `${process.env.REACT_APP_REST_BASE_URL}/Reports/orders_line_items`;
// Hardcoded to current-year (2026) per the requirement "only orders from this
// year, not from the prior year". If/when we add a date picker to the
// Reporting UI this becomes a state value instead.
const ORDERS_REPORT_YEAR_START = "2026-01-01T00:00:00.000Z";
const INVENTORY_COUNTS_PAGE_SIZE_EXPORT = 10000;
const SHIPPING_PORTAL_PAGE_SIZE = 200;
const SHIPPING_FEDEX_BATCH_SIZE = 20;

function toCsvRow(values: (string | number | boolean | null | undefined)[]): string {
  return values
    .map((v) => {
      const s = v == null ? "" : String(v);
      const needsQuotes = /[",\n\r]/.test(s);
      return needsQuotes ? `"${s.replace(/"/g, '""')}"` : s;
    })
    .join(",");
}

function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const ReportGenerator: React.FC = () => {
  const [report, setReport] = React.useState("");
  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [running, setRunning] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [inventoryCountsList, setInventoryCountsList] = React.useState<InventoryCountListItem[]>([]);
  const [selectedInventoryCountId, setSelectedInventoryCountId] = React.useState<string>("");
  const [loadingInventoryCounts, setLoadingInventoryCounts] = React.useState(false);

  const isInventoryCountsReport = report === REPORTS_INVENTORY_COUNTS;

  React.useEffect(() => {
    if (!isInventoryCountsReport) {
      setInventoryCountsList([]);
      setSelectedInventoryCountId("");
      return;
    }
    setSelectedInventoryCountId("");
    setLoadingInventoryCounts(true);
    axios
      .get<InventoryCountListResponse>(baseUrl.INVENTORY_COUNTS, { headers: { accept: "application/json" } })
      .then((res) => setInventoryCountsList(res.data?.data ?? []))
      .catch(() => setInventoryCountsList([]))
      .finally(() => setLoadingInventoryCounts(false));
  }, [isInventoryCountsReport]);

  const runReport = async () => {
    if (report === REPORTS_ORDERS) {
      // Orders report = same CSV shape as the Orders tab's existing Export
      // button (it calls /Orders/export?custom=true), filtered to current-year
      // orders only. Backend builds the CSV; we just download what it returns.
      //
      // We deliberately DON'T pass `offsite` so both in-store AND off-site
      // orders are included — Orders tab's export hardcodes `offsite=false`,
      // but for reporting we want the full picture.
      setError(null);
      setRunning(true);
      try {
        // End is "right now" so a mid-day export captures everything up to the
        // request moment. UTC because the backend stores DateTimeCreated in
        // datetimeoffset(7) and our hourly window doesn't need second-precision.
        const endIso = new Date().toISOString();
        const params = new URLSearchParams({
          start: ORDERS_REPORT_YEAR_START,
          end: endIso,
          custom: "true",
        });
        const url = `${ordersExportBaseUrl}?${params.toString()}`;
        // responseType: "blob" because the endpoint returns text/csv directly
        // (FileResult on the controller side). Treating it as text would also
        // work but blob preserves any future encoding nuances.
        const { data } = await axios.get<Blob>(url, {
          responseType: "blob",
          headers: { accept: "text/csv" },
        });
        const blob = data instanceof Blob ? data : new Blob([data], { type: "text/csv;charset=utf-8;" });
        const downloadUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = `orders_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(downloadUrl);
      } catch (err) {
        setError(axios.isAxiosError(err) ? (err.response?.data?.message ?? err.message) : "Failed to run report.");
      } finally {
        setRunning(false);
      }
      return;
    }

    if (report === REPORTS_PRODUCT_INVENTORY) {
      // Product Inventory reuses the Products tab's existing /products/export
      // endpoint (see productInventoryBaseUrl above). That endpoint already
      // returns the full per-SKU CSV — SKU, Price, ProductDescription,
      // ProductImageUrl, QtyReceived, QtySold, QtyInStock, MediaId, Offsite,
      // SupplierName, CreatedBy — so there's no reason to maintain a parallel
      // codepath. We download the blob and save it with a report-themed
      // filename; no client-side CSV assembly.
      setError(null);
      setRunning(true);
      try {
        const { data } = await axios.get<Blob>(productInventoryBaseUrl, {
          responseType: "blob",
          headers: { accept: "text/csv" },
        });
        const blob = data instanceof Blob ? data : new Blob([data], { type: "text/csv;charset=utf-8;" });
        const downloadUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = `product_inventory_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(downloadUrl);
      } catch (err) {
        setError(axios.isAxiosError(err) ? (err.response?.data?.message ?? err.message) : "Failed to run report.");
      } finally {
        setRunning(false);
      }
      return;
    }

    if (report === REPORTS_FINANCIAL_REPORT) {
      // Financial Report = the per-line CSV built by usp_GetFinancialReport.
      // Backend returns text/csv directly (FileResult on ReportsController);
      // we just blob-download. Date range params are intentionally omitted —
      // the SP defaults to the current calendar year (UTC) when null, which
      // is what Amanda asked for. If/when the date pickers above get
      // uncommented we'll forward startDate/endDate here.
      setError(null);
      setRunning(true);
      try {
        const { data } = await axios.get<Blob>(financialReportBaseUrl, {
          responseType: "blob",
          headers: { accept: "text/csv" },
        });
        const blob = data instanceof Blob ? data : new Blob([data], { type: "text/csv;charset=utf-8;" });
        const downloadUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = `financial_report_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(downloadUrl);
      } catch (err) {
        setError(axios.isAxiosError(err) ? (err.response?.data?.message ?? err.message) : "Failed to run report.");
      } finally {
        setRunning(false);
      }
      return;
    }

    if (report === REPORTS_OFFSITE_ORDERS) {
      setError(null);
      setRunning(true);
      try {
        // Date defaults: current year start through now. The startDate/endDate
        // state values come from the commented-out date pickers, so today they
        // stay empty strings — without explicit defaults the API was returning
        // every offsite order since the dawn of time, including last year's.
        //
        // If/when the date pickers get uncommented above, the user-typed value
        // takes precedence over the defaults.
        const nowDate = new Date();
        const effectiveStart = startDate
          ? `${startDate}T00:00:00.000Z`
          : `${nowDate.getUTCFullYear()}-01-01T00:00:00.000Z`;
        const effectiveEnd = endDate
          ? `${endDate}T23:59:59.999Z`
          : nowDate.toISOString();
        const params = new URLSearchParams({
          startDate: effectiveStart,
          endDate: effectiveEnd,
        });
        const url = `${offsiteOrdersBaseUrl}?${params.toString()}`;
        const { data } = await axios.get<OffsiteOrderRow[]>(url, { headers: { accept: "application/json" } });
        const headers = [
          "orderId", "orderIdentifier", "dealerId", "masterDealerId", "total", "coopUsed", "creditCardUsed",
          "ship", "shippingAddress", "shippingCity", "shippingState", "shippingZip", "dateTimeCreated",
          "trackingNumber", "couponUsed", "claimFee", "shippingFee", "discount", "discountComment",
          "estimatedDeliveryDate", "numberOfBoxes", "status", "offsite"
        ];
        const rows = [headers.join(","), ...data.map((row) => toCsvRow([
          row.orderId, row.orderIdentifier, row.dealerId, row.masterDealerId, row.total, row.coopUsed, row.creditCardUsed,
          row.ship, row.shippingAddress, row.shippingCity, row.shippingState, row.shippingZip, row.dateTimeCreated,
          row.trackingNumber, row.couponUsed, row.claimFee, row.shippingFee, row.discount, row.discountComment ?? "",
          row.estimatedDeliveryDate, row.numberOfBoxes, row.status, row.offsite
        ]))];
        downloadCsv(`offsite_orders_${[startDate, endDate].filter(Boolean).join("_to_") || "all"}.csv`, rows.join("\r\n"));
      } catch (err) {
        setError(axios.isAxiosError(err) ? (err.response?.data?.message ?? err.message) : "Failed to run report.");
      } finally {
        setRunning(false);
      }
      return;
    }

    if (report === REPORTS_INVENTORY_COUNTS) {
      if (!selectedInventoryCountId) {
        setError("Please select an inventory count.");
        return;
      }
      setError(null);
      setRunning(true);
      try {
        const url = `${baseUrl.INVENTORY_COUNTS}/getproducts?inventoryCountId=${selectedInventoryCountId}&pageNum=1&pageSize=${INVENTORY_COUNTS_PAGE_SIZE_EXPORT}`;
        const { data } = await axios.get<{ data?: InventoryCountProductRow[] }>(url, { headers: { accept: "application/json" } });
        const rows = data?.data ?? [];
        const headers = ["productId", "productDescription", "sku", "expectedCount", "totalCounted", "discrepancy"];
        const csvRows = [
          headers.join(","),
          ...rows.map((row) => {
            const expected = row.inventoryCount ?? 0;
            const totalCounted = (row.inventoryCountWithInventoryCountProducts ?? []).reduce((sum, p) => sum + (p.counted ?? 0), 0);
            const discrepancy = totalCounted - expected;
            return toCsvRow([
              row.productId,
              row.productDescription ?? "",
              row.sku ?? "",
              expected,
              totalCounted,
              discrepancy
            ]);
          })
        ];
        const selectedName = inventoryCountsList.find((c) => String(c.inventoryCountId) === selectedInventoryCountId)?.name ?? selectedInventoryCountId;
        const safeName = selectedName.replace(/[^a-zA-Z0-9-_]/g, "_").slice(0, 80);
        downloadCsv(`inventory_count_${safeName}_${selectedInventoryCountId}.csv`, csvRows.join("\r\n"));
      } catch (err) {
        setError(axios.isAxiosError(err) ? (err.response?.data?.message ?? err.message) : "Failed to run report.");
      } finally {
        setRunning(false);
      }
      return;
    }

    if (report === REPORTS_SHIPPING_REPORTS) {
      setError(null);
      setRunning(true);
      try {
        const ordersRes = await axios.get<{ data?: OrderWithOrderBox[] }>(
          `${baseUrl.ORDER}/withorderboxes?pageNum=1&pageSize=${SHIPPING_PORTAL_PAGE_SIZE}&sortType=desc`,
          { headers: { accept: "application/json" } }
        );
        const orders = ordersRes.data?.data ?? [];
        if (orders.length === 0) {
          const headers = ["Status", "Recipient Name", "Company", "Recipient City", "Order #", "Tracking ID", "Location"];
          downloadCsv("shipping_portal.csv", headers.join(",") + "\r\n");
          setRunning(false);
          return;
        }
        const orderIds = orders.map((o) => o.orderId).filter(Boolean);
        const boxesResponses = await Promise.all(
          orderIds.map((orderId) => axios.get<OrderTrackApiResponse[]>(`${baseUrl.ORDER}/getorderboxes/${orderId}`))
        );
        const boxesByOrderId = new Map<number, OrderTrackApiResponse[]>();
        orderIds.forEach((id, i) => {
          const boxes = boxesResponses[i]?.data ?? [];
          boxesByOrderId.set(id, Array.isArray(boxes) ? boxes : []);
        });
        const allTrackingNumbers = Array.from(boxesByOrderId.values()).flatMap((boxes) =>
          boxes.map((b) => b.trackingNumber).filter(Boolean)
        );
        const uniqueTracking = Array.from(new Set(allTrackingNumbers));
        const trackingToStatus: Record<string, { status: string; location: string }> = {};
        for (let i = 0; i < uniqueTracking.length; i += SHIPPING_FEDEX_BATCH_SIZE) {
          const batch = uniqueTracking.slice(i, i + SHIPPING_FEDEX_BATCH_SIZE);
          const fedExResults = await Promise.all(
            batch.map((tn) =>
              axios.get<TrackingRecord>(`${baseUrl.FedEx}/trackpackage?trackingNumber=${encodeURIComponent(tn)}`).catch(() => null)
            )
          );
          batch.forEach((tn, j) => {
            const res = fedExResults[j]?.data?.trackingItems;
            const item = res?.find((t) => t.trackingNumber === tn);
            trackingToStatus[tn] = item ? { status: item.status ?? "", location: item.location ?? "" } : { status: "", location: "" };
          });
        }
        const headers = ["Status", "Recipient Name", "Company", "Recipient City", "Order #", "Tracking ID", "Location"];
        const csvRows: string[] = [headers.join(",")];
        for (const order of orders) {
          const boxes = boxesByOrderId.get(order.orderId!) ?? [];
          const recipientName = [order.firstName, order.lastName].filter(Boolean).join(" ").trim();
          const cityState = [order.shippingCity, order.shippingState].filter(Boolean).join(", ");
          if (boxes.length === 0) {
            csvRows.push(toCsvRow(["", recipientName, order.company ?? "", cityState, order.orderIdentifier ?? order.orderId ?? "", "", ""]));
          } else {
            for (const box of boxes) {
              const info = trackingToStatus[box.trackingNumber] ?? { status: box.deliveryStatus ?? "", location: box.location ?? "" };
              csvRows.push(
                toCsvRow([
                  info.status,
                  recipientName,
                  order.company ?? "",
                  cityState,
                  order.orderIdentifier ?? order.orderId ?? "",
                  box.trackingNumber ?? "",
                  info.location
                ])
              );
            }
          }
        }
        downloadCsv(`shipping_portal_${new Date().toISOString().slice(0, 10)}.csv`, csvRows.join("\r\n"));
      } catch (err) {
        setError(axios.isAxiosError(err) ? (err.response?.data?.message ?? err.message) : "Failed to run report.");
      } finally {
        setRunning(false);
      }
      return;
    }

    setError("This report type is not implemented yet.");
  };

  return (
    <Box sx={{ width: 500, maxWidth: '100%' }}>
      <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#2d3748', mb: 2 }}>
        CREATE A REPORT
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <FormControl sx={{ minWidth: 320 }} size="medium">
          <Select
            displayEmpty
            value={report}
            onChange={e => setReport(e.target.value)}
            sx={{ background: '#fff', borderRadius: 1, height: 48, fontSize: 16 }}
            renderValue={selected =>
              selected ? (
                <span style={{ color: valueColor }}>{selected as string}</span>
              ) : (
                <span style={{ color: placeholderColor }}>Select a report</span>
              )
            }
          >
            <MenuItem value="" disabled>
              <span style={{ color: placeholderColor }}>Select a report</span>
            </MenuItem>
            {REPORTS.map(r => (
              <MenuItem value={r} key={r}>{r}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Start and End date for Offsite Orders - commented out for now
        {!isInventoryCountsReport && (
          <>
        <TextField type="date" value={startDate} onChange={e => setStartDate(e.target.value)} placeholder="Start Date" InputLabelProps={{ shrink: false }} sx={{ minWidth: 150, background: '#fff', borderRadius: 1, height: 48, '& input': { color: startDate ? valueColor : placeholderColor, fontSize: 16, padding: '12.5px 14px', cursor: 'pointer' } }} InputProps={{ onClick: (e) => { const input = e.target as HTMLInputElement; if (input.type === 'date') input.showPicker(); } }} />
        <TextField type="date" value={endDate} onChange={e => setEndDate(e.target.value)} placeholder="End Date" InputLabelProps={{ shrink: false }} sx={{ minWidth: 150, background: '#fff', borderRadius: 1, height: 48, '& input': { color: endDate ? valueColor : placeholderColor, fontSize: 16, padding: '12.5px 14px', cursor: 'pointer' } }} InputProps={{ onClick: (e) => { const input = e.target as HTMLInputElement; if (input.type === 'date') input.showPicker(); } }} />
          </>
        )}
        */}
        {isInventoryCountsReport && (
          <FormControl sx={{ minWidth: 320 }} size="medium" required>
            <Select
              displayEmpty
              value={selectedInventoryCountId}
              onChange={e => setSelectedInventoryCountId(e.target.value)}
              sx={{ background: '#fff', borderRadius: 1, height: 48, fontSize: 16 }}
              renderValue={(selected) =>
                selected ? (
                  <span style={{ color: valueColor }}>
                    {inventoryCountsList.find(c => String(c.inventoryCountId) === selected)?.name ?? selected}
                  </span>
                ) : (
                  <span style={{ color: placeholderColor }}>
                    {loadingInventoryCounts ? "Loading…" : "Select inventory count"}
                  </span>
                )
              }
              disabled={loadingInventoryCounts}
            >
              <MenuItem value="" disabled>
                <span style={{ color: placeholderColor }}>Select inventory count</span>
              </MenuItem>
              {inventoryCountsList.map((c) => (
                <MenuItem key={c.inventoryCountId} value={String(c.inventoryCountId)}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
      {error && (
        <Typography sx={{ mt: 2, color: 'error.main', fontSize: 14 }}>{error}</Typography>
      )}
      <Button
        variant="contained"
        sx={{ mt: 3, background: '#0066ff', borderRadius: 2, fontWeight: 700, fontSize: 16, px: 4, py: 1.5, boxShadow: 'none', textTransform: 'none' }}
        disableElevation
        disabled={running || (isInventoryCountsReport && !selectedInventoryCountId)}
        onClick={runReport}
      >
        {running ? "Running…" : "Run Report"}
      </Button>
    </Box>
  );
};

export default ReportGenerator; 